"use client";

import { useState, useRef, useCallback } from "react";
import { IoNotificationsOutline as BellIcon } from "react-icons/io5";
import {
  Badge,
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
  ScrollShadow,
  Spinner,
} from "@heroui/react";
import { IoIosNotifications } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import { useInfiniteExpiringFDs } from "../../hooks/api/use-common";
import { useMemo } from "react";

export default function InvestmentNotificationIcon() {
  const [isOpen, setIsOpen] = useState(false);
  const scrollRef = useRef(null);
  const navigate = useNavigate();

  // Load read notifications from localStorage
  const getReadNotifications = useCallback(() => {
    if (typeof window !== "undefined") {
      const readNotifications = localStorage.getItem(
        "readInvestmentNotifications"
      );
      return readNotifications ? JSON.parse(readNotifications) : [];
    }
    return [];
  }, []);

  // Save read notifications to localStorage
  const saveReadNotifications = useCallback((ids) => {
    if (typeof window !== "undefined") {
      localStorage.setItem("readInvestmentNotifications", JSON.stringify(ids));
    }
  }, []);

  const formatNotification = useCallback((fdData) => {
    const readNotifications = getReadNotifications();
    const isRead = readNotifications.includes(fdData.id);

    return {
      id: fdData.id,
      title: "FD Maturity Reminder",
      description: `Fixed Deposit #${fdData.fd_number} of Rs. ${fdData.fd_amount
        } matures on ${new Date(
          fdData.maturity_date
        ).toLocaleDateString()}. Branch: ${fdData.our_branch}`,
      time: new Date(fdData.reg_date).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
      date: new Date(fdData.reg_date).toLocaleDateString(),
      read: isRead,
    };
  }, [getReadNotifications]);

  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: loading,
    refetch,
  } = useInfiniteExpiringFDs({ rowsPerpage: 20 });

  // Simplified notification data from infinite query
  const notificationData = useMemo(() => {
    if (!infiniteData) return [];
    return infiniteData.pages.flatMap((page) =>
      (page?.data?.data || []).map(formatNotification)
    );
  }, [infiniteData, formatNotification]);

  const totalNotifications = infiniteData?.pages[0]?.data?.total || 0;

  const handleScroll = useCallback(() => {
    if (!scrollRef.current || isFetchingNextPage || !hasNextPage) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    if (scrollHeight - (scrollTop + clientHeight) < 50) {
      fetchNextPage();
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  const markAsRead = (id) => {
    const readNotifications = getReadNotifications();
    if (!readNotifications.includes(id)) {
      const updatedReadNotifications = [...readNotifications, id];
      saveReadNotifications(updatedReadNotifications);
      // Invalidate query to refresh the list (which will update the read status via formatNotification)
      refetch();
    }
  };

  // Calculate unread count
  const unreadCount = Math.max(
    0,
    totalNotifications - getReadNotifications().length
  );

  const handleOpenChange = (open) => {
    setIsOpen(open);
    if (open) {
      refetch();
    }
  };

  return (
    <Popover
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      placement="bottom-end"
    >
      <PopoverTrigger>
        <Button
          radius="full"
          isIconOnly
          aria-label="Investment Notifications"
          className="relative bg-transparent hover:bg-gray-200 !overflow-visible"
          variant="light"
        >
          <Badge
            content={unreadCount > 9 ? "9+" : unreadCount}
            shape="circle"
            size="lg"
            className="scale-80"
            color="danger"
            isInvisible={unreadCount <= 0}
          >
            <IoIosNotifications className="text-2xl text-blue-700 text-black/70" />
          </Badge>
        </Button>
      </PopoverTrigger>

      {isOpen && (
        <PopoverContent className="w-[380px] p-0 shadow-lg">
          <div className="flex items-center justify-between p-4 border-b w-full">
            <h3 className="font-semibold text-lg">
              Investment Alerts {unreadCount > 0 && `(${unreadCount})`}
            </h3>
            <Button
              size="sm"
              variant="light"
              onPress={() => {
                refetch();
              }}
              disabled={loading}
            >
              Refresh
            </Button>
          </div>

          <ScrollShadow
            ref={scrollRef}
            className="max-h-[400px] overflow-y-auto"
            onScroll={handleScroll}
          >
            {notificationData.length === 0 && !loading ? (
              <div className="flex flex-col items-center justify-center p-6 text-center">
                <BellIcon className="text-default-400 mb-2 h-10 w-10" />
                <p className="text-default-500 text-sm">
                  No notifications available
                </p>
              </div>
            ) : (
              <>
                {notificationData.map((notification) => (
                  <div
                    key={notification.id}
                    className={`cursor-pointer p-4 hover:bg-default-100 transition-all ${!notification.read ? "bg-blue-50" : ""
                      }`}
                    onClick={() => {
                      markAsRead(notification.id);
                      navigate(`/fd/memo/edit/renew/${notification.id}`);
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div className="grid gap-1">
                        <div className="flex items-center gap-2">
                          <div className="font-medium">
                            {notification.title}
                          </div>
                          {!notification.read && (
                            <Badge color="primary" size="sm">
                              New
                            </Badge>
                          )}
                        </div>
                        <div className="text-default-500 text-sm">
                          {notification.description}
                        </div>
                        <div className="text-default-400 text-xs">
                          {notification.date} at {notification.time}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex justify-center p-4">
                    <Spinner size="sm" />
                  </div>
                )}
                {!hasNextPage && notificationData.length > 0 && (
                  <div className="text-center text-default-500 text-sm p-4">
                    No more notifications
                  </div>
                )}
              </>
            )}
          </ScrollShadow>
        </PopoverContent>
      )}
    </Popover>
  );
}
